import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@quake/shared-ui/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@quake/shared-ui/components/ui/popover";
import { Button } from "@quake/shared-ui/components/ui/button";
import { cn } from "@quake/shared-ui/lib/utils";
import CheckIcon from "lucide-react/dist/esm/icons/check";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import { trpc } from "~/utils/trpc";
import UserIcon from "lucide-react/dist/esm/icons/user";

export type UserOption = {
  id: string; // user_datasets.id (dataset-member id)
  name?: string | null;
  email?: string | null;
};

type UserPickerProps = {
  id?: string;
  value: UserOption[];
  onValueChange: (next: UserOption[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  clearable?: boolean;
  excludeMe?: boolean;
};

function formatSelectedUsersDisplay(users: UserOption[], currentUserId?: string) {
  if (!users || users.length === 0) return "add people";
  const names = users.map((u) => {
    const base = u.name && u.name.trim().length > 0 ? u.name : u.email;
    if (currentUserId && u.id === currentUserId) return "you";
    return base ?? "Unknown";
  });

  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  // 3 or more: join with commas and an "and" before last
  return names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}

export function UserPicker({
  id,
  value,
  onValueChange,
  placeholder = "add people",
  disabled = false,
  className,
  searchable = true,
  clearable = true,
  excludeMe = false,
}: UserPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const { data: session } = useQuery(trpc.auth.getSession.queryOptions());

  // Load users in current dataset (paginated, fetch a reasonably large page and filter client-side)
  const {
    data: usersPage,
    status,
    error,
  } = useQuery(trpc.users.getUsersByDatasets.queryOptions({ pagination: { page: 1, pageSize: 500 } }));

  const allUsers: UserOption[] = useMemo(() => {
    const raw = usersPage?.data ?? [];
    return raw.filter((u) => u.invitation !== false).map((u) => ({ id: u.id, name: u.name, email: u.email }));
  }, [usersPage]);

  const users = useMemo(() => {
    return allUsers.filter((u) => !excludeMe || u.id !== session?.user?.user_dataset_id);
  }, [allUsers, excludeMe, session?.user?.user_dataset_id]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => (u.name ?? "").toLowerCase().includes(q) || (u.email ?? "").toLowerCase().includes(q));
  }, [users, search]);

  const isSelected = (u: UserOption) => value.some((s) => s.id === u.id);
  const toggleUser = (u: UserOption) => {
    if (isSelected(u)) onValueChange(value.filter((s) => s.id !== u.id));
    else onValueChange([...value, u]);
  };
  const clearSelection = () => onValueChange([]);

  // Enrich value with full user data from the loaded users list
  const enrichedValue = useMemo(() => {
    return value.map((v) => {
      const fullUser = allUsers.find((u) => u.id === v.id);
      return fullUser ?? v;
    });
  }, [value, allUsers]);

  const displayText = useMemo(
    () => formatSelectedUsersDisplay(enrichedValue, session?.user?.user_dataset_id ?? undefined),
    [enrichedValue, session?.user?.user_dataset_id],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-7 px-2 py-1 w-auto inline-flex items-center gap-2 whitespace-nowrap text-left font-normal",
            !value.length && "text-muted-foreground",
            className,
          )}
          disabled={disabled}
        >
          <span className={cn("text-foreground/90")}>@</span>
          <span className="truncate">{value.length ? displayText : placeholder}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="start">
        <Command shouldFilter={false}>
          {searchable && (
            <CommandInput placeholder="Search people..." value={search} onValueChange={setSearch} className="h-9" />
          )}
          <CommandList>
            {status === "pending" && (
              <div className="py-6 text-center text-sm">
                <div className="flex items-center justify-center gap-2">
                  <Loader2Icon className="h-4 w-4 motion-preset-spin" />
                  <span>Loading people…</span>
                </div>
              </div>
            )}

            {status !== "pending" && filtered.length === 0 && !error && (
              <CommandEmpty>{search ? "No people found." : "Start typing to search people."}</CommandEmpty>
            )}

            {status !== "pending" && filtered.length > 0 && (
              <>
                {clearable && value.length > 0 && (
                  <CommandItem onSelect={clearSelection} className="text-muted-foreground">
                    Clear selection
                  </CommandItem>
                )}
                <CommandGroup>
                  {filtered.map((u) => (
                    <CommandItem
                      key={u.id}
                      onSelect={() => toggleUser(u)}
                      className="flex items-center gap-2"
                      value={`${u.name ?? u.email}-${u.id}`}
                    >
                      <UserIcon className="h-4 w-4 opacity-60" />
                      <div className="flex-1 truncate">
                        <div className="font-medium truncate">{u.name ?? u.email}</div>
                        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      </div>
                      {isSelected(u) && <CheckIcon className="h-4 w-4" />}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {error && (
              <CommandEmpty>
                Error loading people: <br />
                {error.message}
              </CommandEmpty>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
